import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Layout2 from '../../components/global/layout2';
import ApiClient from '../../methods/api/apiClient';

const Tracking = () => {
  const [loads, setLoads] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  const trackingColors = { live: '#27ae60', stale: '#f39c12', offline: '#e74c3c', no_gps: '#95a5a6' };

  const fetchFleet = useCallback(() => {
    ApiClient.get('fleet/tracking').then(res => {
      if (res?.success) {
        setLoads(res.data || []);
        setSummary(res.summary || {});
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchFleet();
    const interval = setInterval(fetchFleet, 30000);
    return () => clearInterval(interval);
  }, [fetchFleet]);

  const mappable = loads.filter(l => l.last_gps_lat && l.last_gps_lng);

  const timeAgo = (d) => {
    if (!d) return 'N/A';
    const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (m < 1) return 'Now';
    if (m < 60) return m + 'm';
    const h = Math.floor(m / 60);
    if (h < 24) return h + 'h';
    return Math.floor(h / 24) + 'd';
  };

  return (
    <Layout2 title="Fleet Tracking">
      <div style={{ padding: '0 16px 16px' }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {[
            { label: 'Active', val: summary.total, color: '#3E549D' },
            { label: 'In Transit', val: summary.in_transit, color: '#27ae60' },
            { label: 'Picked Up', val: summary.pickedup, color: '#f39c12' },
            { label: 'Live GPS', val: summary.live_tracking, color: '#27ae60' },
            { label: 'No GPS', val: summary.no_gps, color: '#e74c3c' }
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, background: '#fff', borderRadius: 6, padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.val || 0}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Map (Leaflet + OpenStreetMap — free, no API key) */}
        <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fa fa-spinner fa-spin fa-2x"></i>
            </div>
          ) : (
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={5}
              style={{ width: '100%', height: '500px' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
                subdomains={['a', 'b', 'c', 'd']}
                maxZoom={19}
              />
              {mappable.map(load => (
                <CircleMarker
                  key={load.id}
                  center={[parseFloat(load.last_gps_lat), parseFloat(load.last_gps_lng)]}
                  radius={10}
                  pathOptions={{
                    color: '#fff',
                    weight: 2,
                    fillColor: trackingColors[load.tracking_status] || '#95a5a6',
                    fillOpacity: 1
                  }}
                >
                  <Popup>
                    <div style={{ fontSize: 12 }}>
                      <strong style={{ color: '#3E549D' }}>#{load.load_id}</strong>
                      <div>{load.carrier?.name || 'N/A'}</div>
                      <div>{load.origin_address} &rarr; {load.destination_address}</div>
                      <div style={{ color: '#999', marginTop: 4 }}>GPS: {timeAgo(load.last_gps_at)}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Legend */}
        <div style={{ padding: '12px 0', display: 'flex', gap: 20, fontSize: 12, color: '#666' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#27ae60', marginRight: 6 }}></span>Live</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#f39c12', marginRight: 6 }}></span>Stale</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#e74c3c', marginRight: 6 }}></span>Offline</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#95a5a6', marginRight: 6 }}></span>No GPS</span>
        </div>
      </div>
    </Layout2>
  );
};

export default Tracking;
